import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { trackFeatureUsage } from '../services/api';
import axios from 'axios';
import { 
  Timer,
  Calendar,
  MapPin,
  Users,
  Trophy,
  Share2,
  Copy,
  Check,
  ChevronRight,
  Flame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CountdownDisplay = ({ days, isToday, isPast }) => {
  if (isPast) {
    return (
      <div className="text-center">
        <p className="font-heading text-2xl text-zinc-500">COMPLETED</p>
      </div>
    );
  }
  
  if (isToday) {
    return (
      <div className="text-center">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <p className="font-heading text-4xl text-red-500">FIGHT DAY!</p>
        </motion.div>
        <p className="text-zinc-400 mt-2">It's go time!</p>
      </div>
    );
  }

  return (
    <div className="text-center">
      <div className="flex items-baseline justify-center gap-2">
        <span className="font-heading text-6xl sm:text-7xl text-white">{days}</span>
        <span className="font-heading text-2xl text-zinc-500">DAYS</span>
      </div>
      <p className="text-zinc-500 mt-2">until fight night</p>
    </div>
  );
};

const FightCountdownCard = ({ fight, onShare }) => {
  const getUrgencyColor = (days) => {
    if (days <= 7) return 'border-red-600/50 bg-red-600/5';
    if (days <= 30) return 'border-yellow-600/50 bg-yellow-600/5';
    return 'border-ba-border';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "bg-ba-surface border rounded-sm overflow-hidden",
        getUrgencyColor(fight.days_until)
      )}
    >
      {/* Countdown Header */}
      <div className="p-6 border-b border-ba-border">
        <CountdownDisplay 
          days={fight.days_until} 
          isToday={fight.days_until === 0}
          isPast={fight.days_until < 0}
        />
      </div>

      {/* Fight Details */}
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Fighter</p>
            <p className="font-heading text-xl text-white">{fight.fighter_name || 'TBA'}</p>
            <p className="text-zinc-500 text-sm">{fight.fighter_weight_class}</p>
          </div>
          <div className="text-3xl text-zinc-600">VS</div>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">Opponent</p>
            <p className="font-heading text-xl text-white">{fight.opponent_name || fight.opponent || 'TBA'}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-ba-border space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Trophy className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-zinc-400">{fight.event_name || 'Event TBA'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Calendar className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-zinc-400">
              {new Date(fight.fight_date).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'numeric',
                day: 'numeric',
                year: 'numeric'
              })}
            </span>
          </div>
          {fight.location && (
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-zinc-400">{fight.location}</span>
            </div>
          )}
          {fight.camp_name && (
            <div className="flex items-center gap-3 text-sm">
              <Flame className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="text-zinc-400">Camp: {fight.camp_name}</span>
            </div>
          )}
        </div>

        <Button
          onClick={() => onShare(fight)}
          variant="outline"
          className="w-full mt-4 border-zinc-700 text-zinc-300 hover:bg-zinc-900"
        >
          <Share2 className="w-4 h-4 mr-2" />
          Share Countdown
        </Button>
      </div>
    </motion.div>
  );
};

const FightCountdownPage = () => {
  const [fights, setFights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFight, setSelectedFight] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadUpcomingFights();
    trackFeatureUsage('fight_countdown', 'view').catch(() => {});
  }, []);

  const loadUpcomingFights = async () => {
    try {
      const response = await axios.get(`${API}/api/fights/upcoming`);
      setFights(response.data);
    } catch (error) {
      console.error('Failed to load upcoming fights');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (fight) => {
    setSelectedFight(fight);
    trackFeatureUsage('fight_countdown', 'share', { fight_id: fight.id }).catch(() => {});
  };

  const handleCopyWidget = () => {
    const widgetCode = `<!-- Black Arrow MMA Fight Countdown -->
<div id="ba-countdown-${selectedFight.id}"></div>
<script src="${window.location.origin}/widgets/countdown.js" data-fight-id="${selectedFight.id}"></script>`;
    
    navigator.clipboard.writeText(widgetCode);
    setCopied(true);
    toast.success('Widget code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareSocial = (platform) => {
    const opponentName = selectedFight.opponent_name || selectedFight.opponent || 'TBA';
    const text = `${selectedFight.fighter_name} vs ${opponentName} - ${selectedFight.days_until} days until fight night! ${selectedFight.event_name || ''}`;
    const url = window.location.origin;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    
    trackFeatureUsage('fight_countdown', 'share_social', { platform }).catch(() => {});
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="countdown-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Fight Night</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            COUNTDOWN
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Track and share upcoming fight countdowns</p>
        </div>

        {fights.length === 0 ? (
          <div className="bg-ba-surface border border-ba-border rounded-sm p-12 text-center">
            <Timer className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-white mb-2">No Upcoming Fights</h2>
            <p className="text-zinc-500 mb-6">
              Schedule a fight to see the countdown here
            </p>
            <Button
              onClick={() => window.location.href = '/fights'}
              className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule a Fight
            </Button>
          </div>
        ) : (
          <>
            {/* Featured Fight (next upcoming) */}
            {fights[0] && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative bg-gradient-to-br from-red-600/20 via-ba-surface to-ba-surface border border-red-600/30 rounded-sm overflow-hidden"
              >
                {/* Background Effect */}
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-5" />
                
                <div className="relative p-8 md:p-12">
                  <div className="flex items-center gap-2 mb-6">
                    <Flame className="w-5 h-5 text-red-500" />
                    <span className="text-[10px] tracking-[0.3em] uppercase text-red-500">Next Fight</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                    {/* Fighter */}
                    <div className="text-center md:text-left">
                      <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto md:mx-0 mb-4">
                        <Users className="w-10 h-10 text-zinc-500" />
                      </div>
                      <p className="font-heading text-2xl md:text-3xl text-white">{fights[0].fighter_name}</p>
                      <p className="text-zinc-500">{fights[0].fighter_weight_class}</p>
                    </div>
                    
                    {/* Countdown */}
                    <div className="text-center">
                      <CountdownDisplay 
                        days={fights[0].days_until}
                        isToday={fights[0].days_until === 0}
                        isPast={fights[0].days_until < 0}
                      />
                      <div className="mt-6 space-y-2">
                        <p className="text-white font-heading">{fights[0].event_name || 'Event TBA'}</p>
                        <p className="text-zinc-500 text-sm">{new Date(fights[0].fight_date).toLocaleDateString('en-US')}</p>
                      </div>
                    </div>
                    
                    {/* Opponent */}
                    <div className="text-center md:text-right">
                      <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto md:ml-auto mb-4">
                        <Users className="w-10 h-10 text-zinc-500" />
                      </div>
                      <p className="font-heading text-2xl md:text-3xl text-white">{fights[0].opponent_name || fights[0].opponent || 'TBA'}</p>
                      <p className="text-zinc-500">Opponent</p>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      onClick={() => handleShare(fights[0])}
                      className="bg-red-600 hover:bg-red-700 text-white font-heading uppercase tracking-wider"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Countdown
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Other Upcoming Fights */}
            {fights.length > 1 && (
              <div>
                <h2 className="font-heading text-xl text-white mb-4">More Upcoming Fights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {fights.slice(1).map((fight, index) => (
                    <FightCountdownCard 
                      key={fight.id} 
                      fight={fight}
                      onShare={handleShare}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Share Modal */}
        {selectedFight && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-ba-surface border border-ba-border rounded-sm max-w-md w-full p-6"
            >
              <h3 className="font-heading text-xl text-white mb-4">Share Countdown</h3>
              
              <div className="bg-ba-black border border-ba-border rounded-sm p-4 mb-6">
                <p className="text-white font-heading text-lg">
                  {selectedFight.fighter_name} vs {selectedFight.opponent || 'TBA'}
                </p>
                <p className="text-zinc-500 text-sm mt-1">
                  {selectedFight.days_until} days until fight night
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => handleShareSocial('twitter')}
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-900 justify-start"
                >
                  <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  Share on X (Twitter)
                </Button>
                
                <Button
                  onClick={() => handleShareSocial('facebook')}
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-900 justify-start"
                >
                  <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Share on Facebook
                </Button>
                
                <Button
                  onClick={handleCopyWidget}
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-900 justify-start"
                >
                  {copied ? (
                    <Check className="w-5 h-5 mr-3 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5 mr-3" />
                  )}
                  Copy Embed Code
                </Button>
              </div>

              <Button
                onClick={() => setSelectedFight(null)}
                variant="ghost"
                className="w-full mt-4 text-zinc-400"
              >
                Close
              </Button>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FightCountdownPage;
